/** Versioned selection snapshot. Additive fields only; do not change field meaning. */
export const SELECTION_SCHEMA = "pcbx.selection/1" as const;

export const SELECTION_SURFACES = ["schematic", "pcb", "none"] as const;
export type SelectionSurface = (typeof SELECTION_SURFACES)[number];

export const SELECTION_SOURCES = [
  "editor_event",
  "overlay_click",
  "deep_link",
  "agent_focus",
  "cleared",
] as const;
export type SelectionSource = (typeof SELECTION_SOURCES)[number];

export const SELECTION_ITEM_KINDS = [
  "symbol",
  "footprint",
  "pad",
  "label",
  "zone",
  "other",
] as const;
export type SelectionItemKind = (typeof SELECTION_ITEM_KINDS)[number];

export const EDITOR_DOCUMENT_KINDS = ["schematic", "board", "project"] as const;
export type EditorDocumentKind = (typeof EDITOR_DOCUMENT_KINDS)[number];

/** Honest highlight failure when no viewer implements focus. */
export const HIGHLIGHT_UNSUPPORTED_REASON = "viewer_unimplemented" as const;

export type SelectionBBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type SelectionItem = {
  kind: SelectionItemKind;
  refdes: string | null;
  uuid: string | null;
  value: string | null;
  lib_id: string | null;
  net: string | null;
  sheet_path: string | null;
  bbox?: SelectionBBox;
};

/**
 * Cross Think / Review / Hardware selection snapshot.
 * Think consumes this read-only; it does not own a second copy.
 */
export type SelectionSnapshot = {
  schema: typeof SELECTION_SCHEMA;
  at: string;
  surface: SelectionSurface;
  document_path: string | null;
  git_oid: string | null;
  items: SelectionItem[];
  source: SelectionSource;
};

/**
 * Platform-authorized document reference.
 * `src` is a short-TTL signed URL or inline payload issued by the host —
 * this interface does not fetch or hold repository credentials.
 */
export type EditorDocumentRef = {
  path: string;
  kind: EditorDocumentKind;
  src: string;
  name?: string;
};

export type HighlightTarget = {
  refdes?: string;
  uuid?: string;
  document_path?: string;
};

export type HighlightResult =
  | { ok: true }
  | { ok: false; reason: string };

export type SelectionListener = (snapshot: SelectionSnapshot) => void;

/** Think-facing read API. No `set` / `clear`. */
export interface SelectionReader {
  get(): SelectionSnapshot;
  subscribe(listener: SelectionListener): () => void;
}

/**
 * Pluggable viewer host. Implementations live outside this package.
 * Methods must fail visibly — never fake a loaded viewer or a selection.
 */
export interface EditorHost {
  loadDocument(doc: EditorDocumentRef | EditorDocumentRef[]): Promise<void>;
  getSelection(): SelectionSnapshot;
  onSelectionChange(cb: SelectionListener): () => void;
  highlight(target: HighlightTarget): Promise<HighlightResult>;
  dispose(): void;
}
